import React from 'react';
import styled from 'styled-components';
import Modal from '../../Modal/Modal';
import { H2, PSmall } from '../../elements/Text';
import { CloseIcon } from '../../Icons';
import { useGetCompany } from '../../../hooks/use-hydra/admin';
import { FormInfo, FormInput, FormLabel } from '../../Form';
import dayjs from 'dayjs';
import { Dropdown } from '../../elements/Dropdown';
import Button from '../../elements/Button';
import {
  assignLearnersToCohortMentor,
  CompanyResponse,
} from '../../../api/admin';
import { ScrollableTooltip } from '../../Tooltip';
import { useMutation } from 'react-query';
import useAuth from '../../../hooks/use-auth';
import { toast } from 'react-toastify';
import { Input, MultiLineInput } from '../../elements/Input';

const Row = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 16px;
`;

const Wrapper = styled(Modal)`
  padding-bottom: 72px;
  padding-top: 64px;
  width: 80vw;
  min-width: 400px;
  overflow-y: auto;
  background-color: ${({ theme }) => theme.colors.bgPage2};
  border: 1px solid ${({ theme }) => theme.colors.bgActive};
`;

const CloseButton = styled(CloseIcon)`
  padding: 0;
`;

const Header = styled(H2)`
  margin: 0;
  padding: 0;
`;

const DateTimePicker = styled(FormInput).attrs({
  type: 'datetime-local',
})`
  margin-top: 8px;
`;

const Info = styled(FormInfo)`
  margin-top: 8px;
`;

type LearnerSplitType = {
  learnersWithMentors: CompanyResponse['cohorts'][number]['learners'][number][];
  learnersToAssign: CompanyResponse['cohorts'][number]['learners'][number][];
};

export default function AssignLearnersToMentorModal({
  isOpen,
  onClose,
  companyId,
  selectedSessionIds,
  cycleId,
}: {
  isOpen: boolean;
  onClose: () => void;
  companyId: number;
  selectedSessionIds: number[];
  cycleId: number;
}) {
  const { getTokenSilently } = useAuth();
  const {
    data,
    refetch: refetchCompany,
    isLoading: isLoadingCompany,
  } = useGetCompany(companyId);

  const cohort = data?.cohorts.find((c) => c.cycleId === cycleId);
  const cohortValue = {
    label: `${dayjs.utc(cohort?.startDate).format('MMM D, YYYY')} - ${dayjs
      .utc(cohort?.endDate)
      .format('MMM D, YYYY')}`,
    value: cohort?.cycleId,
  };

  const mentorOptions = data?.mentors.map((mentor) => ({
    label: `${mentor.name} - ${mentor.email} [${mentor.topics.join(', ')}]`,
    value: mentor.id,
  }));
  const [selectedMentor, setSelectedMentor] =
    React.useState<{
      label: string;
      value: number;
    } | null>(null);
  const selectedMentorData =
    data?.mentors.find((m) => m.id === selectedMentor?.value) ?? null;

  const on11MeetingTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const date = dayjs(e.target.value);
    const minutes = date.minute();
    const roundedMinutes = Math.round(minutes / 15) * 15;
    const roundedDate = date.minute(roundedMinutes);
    setFirst11MeetingTime(roundedDate.format('YYYY-MM-DDTHH:mm'));
  };
  const [first11MeetingTime, setFirst11MeetingTime] =
    React.useState<string>('');
  const all11MeetingTimes = get11MeetingTimes({
    cohort,
    first11MeetingTime,
  });

  // NOTE - mentor assignment is restricted to 1 learner at the time so availabilities can be shown
  // const learnerSessions = data?.cohorts
  //   .find((c) => c.cycleId === cycleId)
  //   ?.learners.filter(
  //     (l) => l.sessionId && selectedSessionIds.includes(l.sessionId)
  //   );
  // const uniqueModules = [
  //   ...new Set(learnerSessions?.map((l) => l.module.contentTitle)),
  // ];

  // const { learnersWithMentors, learnersToAssign } =
  //   selectedSessionIds.reduce<LearnerSplitType>(
  //     (acc, sessionId) => {
  //       const session = learnerSessions?.find((l) => l.sessionId === sessionId);
  //       if (!session) {
  //         return acc;
  //       }

  //       if (session?.mentor?.assignmentId) {
  //         acc.learnersWithMentors.push(session);
  //       } else {
  //         acc.learnersToAssign.push(session);
  //       }

  //       return acc;
  //     },
  //     {
  //       learnersWithMentors: [],
  //       learnersToAssign: [],
  //     } as LearnerSplitType
  //   );

  // single-mentor logic

  const selectedId = selectedSessionIds[0];
  const learnerSession = data?.cohorts
    .find((c) => c.cycleId === cycleId)
    ?.learners.find((l) => l.sessionId === selectedId);

  const { learnersWithMentors, learnersToAssign } =
    selectedSessionIds.reduce<LearnerSplitType>(
      (acc) => {
        const session = learnerSession;
        if (!session) {
          return acc;
        }

        if (session?.mentor?.assignmentId) {
          acc.learnersWithMentors.push(session);
        } else {
          acc.learnersToAssign.push(session);
        }

        return acc;
      },
      {
        learnersWithMentors: [],
        learnersToAssign: [],
      } as LearnerSplitType
    );
  const uniqueModules = learnerSession
    ? [learnerSession.module.contentTitle]
    : [];

  const { canAssign, tooltip: buttonDisableTooltip } = getAssignButtonState({
    learnersWithMentors,
    learnersToAssign,
    selectedMentor: selectedMentorData,
    first11MeetingTime,
  });

  const assignMentorMutation = useMutation({
    mutationFn: async () => {
      if (!selectedMentorData) {
        throw new Error('Please select a mentor.');
      }
      if (!first11MeetingTime) {
        throw new Error('Please select a first 1:1 meeting time.');
      }
      if (learnersToAssign.length === 0) {
        throw new Error('Please select at least one valid learner.');
      }
      const token = await getTokenSilently();
      return assignLearnersToCohortMentor(token, {
        companyId,
        cohortId: cycleId,
        sessionIds: learnersToAssign
          .map((l) => l.sessionId)
          .filter((id) => id !== null) as number[],
        mentorProfileId: selectedMentorData?.id,
        oneOnOneTimestamps: all11MeetingTimes,
      });
    },
    onSuccess: async () => {
      await refetchCompany();
      toast.success('Learners assigned to mentor successfully');
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Failed to assign learners to mentor'
      );
    },
  });

  const learnerAvailability = learnerSession?.onboardingAnswers?.availability;
  const learnerTimezone = learnerSession?.onboardingAnswers?.timezone;

  if (!isOpen) {
    return null;
  }

  return (
    <Wrapper>
      <Row>
        <Header>Assign Learners to Mentor</Header>
        <CloseButton onClick={onClose} />
      </Row>
      <FormLabel>Cohort</FormLabel>
      <Dropdown
        options={[cohortValue]}
        value={cohortValue}
        isDisabled
        menuPortalTarget={document.body}
      />

      <FormLabel>Mentor</FormLabel>
      <Info>
        The learners you selected onboarded in the following modules:{' '}
        {uniqueModules.join(', ')}.
      </Info>
      <Dropdown
        options={mentorOptions}
        onChange={setSelectedMentor}
        value={selectedMentor}
        placeholder="Select a mentor"
        menuPortalTarget={document.body}
        isMulti={false}
        isClearable
        isValidNewOption={() => false}
      />

      <FormLabel>Learner Availability</FormLabel>
      <MultiLineInput
        style={{ marginTop: '8px' }}
        value={learnerAvailability ?? ''}
        isDisabled
        onChange={() => {}}
        placeholder="Learner availability goes here"
      />
      <FormLabel>Learner Timezone</FormLabel>
      <Input
        style={{ marginTop: '8px' }}
        value={learnerTimezone?.timezone ?? ''}
        isDisabled
        onChange={() => {}}
      />
      <FormLabel>First 1:1 meeting time (UTC)</FormLabel>
      <DateTimePicker
        value={first11MeetingTime}
        onChange={on11MeetingTimeChange}
        min={dayjs.utc(cohort?.startDate).format('YYYY-MM-DDTHH:mm')}
        max={dayjs.utc(cohort?.endDate).format('YYYY-MM-DDTHH:mm')}
      />
      {first11MeetingTime && cohort ? (
        <Info>
          We'll create 1:1 events for every week until the cohort's end, on the
          following dates:{' '}
          {all11MeetingTimes
            .map((time) => dayjs.utc(time).format('MMM D, YYYY HH:mm UTC'))
            .join(', ')}
          .
          <br />
          <br />
          If multiple learners are assigned to the same mentor at the same time,
          their 1:1 meetings will be combined into a single calendar event.
        </Info>
      ) : (
        <Info>
          Select a first 1:1 meeting time and a cohort to preview all the dates.
        </Info>
      )}
      {learnersWithMentors.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-with-mentors">
              {learnersWithMentors.length} learners
            </Bold>{' '}
            with mentors assigned in this cohort. You can't assign a new one to
            them.
          </PSmall>
          <EmailsTooltip
            emails={learnersWithMentors.map((l) => `${l.name} (${l.email})`)}
            buttonId={'learners-with-mentors'}
          />
        </>
      )}
      {learnersToAssign.length > 0 && (
        <>
          <PSmall>
            There are{' '}
            <Bold className="learners-to-assign">
              {learnersToAssign.length} learners
            </Bold>{' '}
            that will be assigned to the mentor. This will create 1:1 Google
            Calendar events, and will send both the learners and the mentor an
            introduction email and slack message.
          </PSmall>
          <EmailsTooltip
            emails={learnersToAssign.map((l) => `${l.name} (${l.email})`)}
            buttonId={'learners-to-assign'}
          />
        </>
      )}
      <Row>
        <Button
          label="Assign Mentor"
          showIconBeforeLabel
          faIcon="fa-solid fa-paper-plane"
          type="primary"
          onClick={() => {
            assignMentorMutation.mutate();
          }}
          isDisabled={!canAssign || assignMentorMutation.isLoading}
          tooltip={buttonDisableTooltip}
          isLoading={assignMentorMutation.isLoading || isLoadingCompany}
        />
      </Row>
    </Wrapper>
  );
}

function get11MeetingTimes({
  cohort,
  first11MeetingTime,
}: {
  cohort: CompanyResponse['cohorts'][number] | undefined;
  first11MeetingTime: string;
}) {
  if (!cohort || !first11MeetingTime) {
    return [];
  }

  const cycleEndUTC = dayjs.utc(cohort.endDate);
  const firstOneOnOneUTC = dayjs.utc(first11MeetingTime);

  if (firstOneOnOneUTC.isAfter(cycleEndUTC)) {
    return [];
  }

  const occurencesCount =
    Math.floor(cycleEndUTC.diff(firstOneOnOneUTC, 'days') / 7) + 1;

  return [...Array(occurencesCount)].map((_, i) => {
    return firstOneOnOneUTC.add(i, 'weeks').toDate();
  });
}

function getAssignButtonState({
  learnersWithMentors,
  learnersToAssign,
  selectedMentor,
  first11MeetingTime,
}: {
  learnersWithMentors: CompanyResponse['cohorts'][number]['learners'][number][];
  learnersToAssign: CompanyResponse['cohorts'][number]['learners'][number][];
  selectedMentor: CompanyResponse['mentors'][number] | null;
  first11MeetingTime: string;
}) {
  if (!selectedMentor) {
    return {
      canAssign: false,
      tooltip: 'Select a mentor.',
    };
  }
  if (!first11MeetingTime) {
    return {
      canAssign: false,
      tooltip: 'Select a first 1:1 meeting time.',
    };
  }
  if (learnersWithMentors.length > 0) {
    return {
      canAssign: false,
      tooltip: 'Some learners already have a mentor assigned in this cohort.',
    };
  }
  if (learnersToAssign.length === 0) {
    return {
      canAssign: false,
      tooltip: 'No learners to assign',
    };
  }
  return {
    canAssign: true,
    tooltip: undefined,
  };
}

const EmailsTooltip = ({
  emails,
  buttonId,
}: {
  emails: string[];
  buttonId: string;
}) => {
  return (
    <ScrollableTooltip
      anchorSelect={`.${buttonId}`}
      place="top"
      delayHide={500}
    >
      <div style={{ overflowY: 'auto' }}>
        <span
          style={{
            whiteSpace: 'pre-wrap',
            overflowY: 'auto',
          }}
        >
          {emails.join('\n')}
        </span>
      </div>
    </ScrollableTooltip>
  );
};

const Bold = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.textPrimary};
  cursor: pointer;
`;
